import Link from 'next/link';
import React from 'react';
import { MY_TEAM_PAGES, MY_TEAM_MAINTENANCE_MESSAGE } from '@lib/constants';
import { useIsAppView } from '@hooks/useIsAppView';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import Maintenance from '@components/maintenace';
import ProfileBootstrap from '@components/my-team/ProfileBootstrap';
import AppUpsell from '@components/app-upsell';
import { BodyRegular } from '@components/atoms/typography';
import Button from '@components/atoms/button';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';

/**
 * A tab's own page or one beneath it, such as a rival opened from Mini
 * Leagues, which keeps the tabs and the FPL-updating screen.
 */
export const isMyTeamTabPath = (pathname: string, slug: string) =>
  pathname === `/my-team/${slug}` || pathname.startsWith(`/my-team/${slug}/`);

type MyTeamTabsProps = {
  urlSlug: string;
  children: React.ReactNode | React.ReactNode[];
  shouldRender: boolean;
};

const MyTeamTabsHOC = ({
  urlSlug,
  children,
  shouldRender
}: MyTeamTabsProps) => {
  const { context } = useLeagueContext();
  const { isAppView } = useIsAppView();
  const nextGameweek = context?.nextGameweek?.number ?? null;

  const fplSeasonIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.fplSeasonIsClosed,
    false
  );

  // Maintenance is driven by /league/context's isFplUpdating server signal,
  // with a PostHog kill switch so an operator can force-enable the banner
  // outside of FPL's update window when we need to gate the page manually.
  const isOperatorForcingMaintenance = usePostHogFeatureFlag(
    PostHogFeatureFlags.myTeamIsInMaintenance,
    false
  );

  const isInMaintenanceMode =
    isOperatorForcingMaintenance || context?.isFplUpdating === true;

  return shouldRender ? (
    <>
      {/* Lazily creates the user's profile (GET /users/me is findOrCreate)
          before any my-team PUT runs, so a brand-new user's import/build
          flow doesn't 404 with PROFILE_NOT_FOUND. */}
      <ProfileBootstrap />
      <AppUpsell />
      <div className="overflow-x-scroll border-b-neutral-300 border-b-[1px] bg-white mb-2 px-2 sm:px-0 md:mb-5 mx-auto lg:container w-100 flex justify-between sm:justify-evenly md:justify-start pt-2 space-x-0 md:space-x-4 px-2 md:px-0 scrolling-touch scrollbar-hide">
        {MY_TEAM_PAGES.map((page, index) => (
          <Link
            key={page.slug}
            href={`/my-team/${page.slug}`}
            data-cy={`my-team-tab-link-${page.slug}`}
            className={`${
              index !== 0 ? 'pl-2' : ''
            } flex flex-none hover:border-b-primary hover:border-b-[3px] 0 p-2 sm:p-3 text-xs sm:text-base text-neutral-600 ${
              isMyTeamTabPath(urlSlug, page.slug)
                ? 'text-primary border-b-[3px] border-b-primary font-bold'
                : 'border-b-neutral-300 font-normal hover:text-neutral-600'
            }`}
            aria-label={`/my-team/${page.slug}`}
          >
            {page.name}
          </Link>
        ))}
      </div>
      {fplSeasonIsClosed ? (
        <EndOfSeasonHoldingMessage pageTitle="My Team" isAppView={isAppView} />
      ) : (
        <>
          {isInMaintenanceMode ? (
            <div className={`flex justify-center items-center `}>
              <Maintenance
                message={MY_TEAM_MAINTENANCE_MESSAGE}
                subMessage={
                  nextGameweek !== null &&
                  nextGameweek > 0 &&
                  nextGameweek < 39 ? (
                    <div className="flex flex-col">
                      <BodyRegular className="text-center mb-5">
                        In the meantime, you can start preparing for the next
                        gameweek using the points prediction tool.{' '}
                      </BodyRegular>
                      {!isAppView && (
                        <div className="flex justify-center">
                          <Button href="/predictions" isLink variant="primary">
                            Go to Points Predictions
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : null
                }
              />
            </div>
          ) : (
            <>{children}</>
          )}
        </>
      )}
    </>
  ) : (
    <>{children}</>
  );
};

export default MyTeamTabsHOC;
